import React, { useState } from 'react';
import {
  Heart, Send, Users, Bell, Sparkles, Calendar, Trophy, ChevronDown,
  ListChecks, Gift, Zap, ShoppingBasket, Repeat, UserCircle, Star,
  Flame, Award, HelpCircle
} from 'lucide-react';
import { submitBetaSignup } from '../../lib/beta.js';
import { DARK_THEME } from '../../lib/theme.js';
import { InstallAppPrompt } from '../../components/InstallAppPrompt.jsx';

export function LandingPage() {
  const theme = DARK_THEME;

  return (
    <div style={{
      minHeight: '100vh',
      background: theme.bg,
      color: theme.text,
      fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
    }}>
      <Header theme={theme} />
      <Hero theme={theme} />
      <ForDigOgJer theme={theme} />
      <HowItWorks theme={theme} />
      <CtaBand theme={theme} variant="middle" />
      <Gamification theme={theme} />
      <Features theme={theme} />
      <CtaBand theme={theme} variant="late" />
      <FAQ theme={theme} />
      <SignupCTA theme={theme} />
      <Footer theme={theme} />
    </div>
  );
}

// ============================================================
// HEADER
// ============================================================

function Header({ theme }) {
  return (
    <header style={{
      position: 'sticky', top: 0, zIndex: 50,
      background: 'rgba(15, 14, 13, 0.85)',
      backdropFilter: 'blur(12px)',
      borderBottom: `1px solid ${theme.border}`,
      padding: '14px 20px',
    }}>
      <div style={{
        maxWidth: 1100,
        margin: '0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <a href="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', color: theme.text }}>
          <Logo size={32} theme={theme} />
          <span style={{ fontSize: 17, fontWeight: 800, letterSpacing: -0.3 }}>Tag Team</span>
        </a>

        <nav style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <a href="#hvordan" style={{ color: theme.textMuted, textDecoration: 'none', fontSize: 13, fontWeight: 500 }}
            className="tt-hide-mobile">Sådan virker det</a>
          <a href="#funktioner" style={{ color: theme.textMuted, textDecoration: 'none', fontSize: 13, fontWeight: 500 }}
            className="tt-hide-mobile">Funktioner</a>
          <a href="#faq" style={{ color: theme.textMuted, textDecoration: 'none', fontSize: 13, fontWeight: 500 }}
            className="tt-hide-mobile">FAQ</a>
          <a href="/login" style={{
            padding: '8px 16px',
            borderRadius: 8,
            background: theme.coral,
            color: 'white',
            textDecoration: 'none',
            fontSize: 13,
            fontWeight: 600,
          }}>Log ind</a>
        </nav>
      </div>

      <style>{`
        @media (max-width: 640px) {
          .tt-hide-mobile { display: none !important; }
        }
      `}</style>
    </header>
  );
}

// ============================================================
// HERO
// ============================================================

function Hero({ theme }) {
  return (
    <section style={{
      padding: '60px 20px 40px',
      maxWidth: 1100,
      margin: '0 auto',
      textAlign: 'center',
    }}>
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 12px',
        background: `${theme.coral}15`,
        border: `1px solid ${theme.coral}30`,
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        color: theme.coral,
        marginBottom: 24,
      }}>
        <Sparkles size={12} /> Lige nu er Tag Team gratis at bruge
      </div>

      <h1 style={{
        margin: 0,
        fontSize: 'clamp(32px, 6vw, 54px)',
        fontWeight: 900,
        lineHeight: 1.05,
        letterSpacing: -1.5,
      }}>
        Træt af at få skældt ud<br />fordi du har glemt<br />at tage skraldet ud..<br />igen?
      </h1>

      <p style={{
        margin: '24px auto 0',
        maxWidth: 580,
        fontSize: 17,
        color: theme.textMuted,
        lineHeight: 1.5,
      }}>
        Tag Team er en todo-app der gør hverdagen til et team-projekt for jer to.
        Send hinanden opgaver, vind kærlighedspoint, og få ros i stedet for skæld ud.
      </p>

      <div style={{
        marginTop: 32,
        display: 'flex',
        gap: 10,
        justifyContent: 'center',
        flexWrap: 'wrap',
      }}>
        <a href="/login" style={primaryCtaStyle(theme)}>
          <Sparkles size={16} /> Kom i gang gratis
        </a>
        <InstallAppPrompt theme={theme} variant="hero" />
        <a href="#hvordan" style={secondaryCtaStyle(theme)}>
          Se hvordan det virker <ChevronDown size={16} />
        </a>
      </div>

      <p style={{
        marginTop: 40,
        fontSize: 12,
        color: theme.textDim,
      }}>
        Get sh*it done · Mindre stress · mere kærlighed
      </p>
    </section>
  );
}

// ============================================================
// FOR DIG — OG FOR JER (NEW SECTION ID 101)
// ============================================================

function ForDigOgJer({ theme }) {
  return (
    <section id="for-dig-og-jer" style={{
      padding: '60px 20px',
      maxWidth: 1100,
      margin: '0 auto',
    }}>
      <SectionTag theme={theme} text="For dig — og for jer" color={theme.gold} />

      <h2 style={sectionHeadingStyle()}>
        To apps i én
      </h2>

      <p style={sectionLeadStyle(theme)}>
        Tag Team er ikke kun en par-app. Det er først og fremmest et personligt todo-program
        der husker alt det du selv skal — og som lige tilfældigvis også kan dele opgaver
        med din partner.
      </p>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: 16,
        marginTop: 40,
      }}>
        <DualCard
          theme={theme}
          tag="TIL DIG SELV"
          tagColor={theme.gold}
          icon={UserCircle}
          title="Glem aldrig en opgave igen"
          text="Hold styr på alt det du skal igennem en uge — uden at det fylder hovedet. Tag Team minder dig om opgaverne når de skal løses, så du har plads til at tænke på noget andet."
          items={[
            'Arbejde og deadlines',
            'Børnenes lektier og fritidsaktiviteter',
            'Vedligehold af hus, bil og have',
            'Rengøring og daglige rutiner',
            'Aftaler med venner og familie',
            'Fødselsdage og mærkedage',
          ]}
        />

        <DualCard
          theme={theme}
          tag="TIL JER SOM PAR"
          tagColor={theme.coral}
          icon={Heart}
          title="Få ting gjort sammen"
          text="Send opgaver til hinanden, del en indkøbsliste, og høst kærlighedspoint når I løser opgaver sammen. Mere teamwork, mindre nag og glemte aftaler."
          items={[
            'Send opgaver med deadline og point',
            'Tilføj belønninger som motivation',
            'Fælles indkøbsliste i realtid',
            'Streaks og achievements',
            'Kærlighedspoint der bygger jer op sammen',
            'Et fælles overblik over hvad der skal gøres',
          ]}
        />
      </div>
    </section>
  );
}

function DualCard({ theme, tag, tagColor, icon: Icon, title, text, items }) {
  return (
    <div style={{
      background: theme.bgCard,
      border: `1px solid ${theme.borderSubtle}`,
      borderRadius: 18,
      padding: 28,
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{
        display: 'inline-flex',
        alignSelf: 'flex-start',
        alignItems: 'center',
        gap: 4,
        padding: '4px 10px',
        background: `${tagColor}18`,
        border: `1px solid ${tagColor}30`,
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 1,
        color: tagColor,
        marginBottom: 16,
      }}>
        {tag}
      </div>

      <div style={{
        width: 48, height: 48,
        borderRadius: 12,
        background: `${tagColor}18`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 14,
      }}>
        <Icon size={22} color={tagColor} />
      </div>

      <h3 style={{ margin: 0, fontSize: 19, fontWeight: 800, letterSpacing: -0.4 }}>{title}</h3>
      <p style={{ margin: '10px 0 16px', fontSize: 14, color: theme.textMuted, lineHeight: 1.55 }}>{text}</p>

      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map((item, i) => (
          <li key={i} style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            fontSize: 13,
            color: theme.text,
            lineHeight: 1.5,
          }}>
            <span style={{
              flexShrink: 0,
              marginTop: 6,
              width: 5, height: 5,
              borderRadius: '50%',
              background: tagColor,
            }}></span>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ============================================================
// HOW IT WORKS (UPDATED ID 102)
// ============================================================

function HowItWorks({ theme }) {
  const steps = [
    {
      icon: Send,
      color: theme.coral,
      title: 'Giv din partner en opgave',
      text: 'Skriv hvad der skal gøres, sæt en deadline, og giv den point efter hvor stor opgaven er. Tilføj en lille besked eller en belønning hvis du vil sætte ekstra krudt på. Når din partner løser opgaven, belønnes han eller hun.',
    },
    {
      icon: ListChecks,
      color: theme.gold,
      title: 'Hold styr på dine egne opgaver',
      text: 'Træt af at glemme at tømme postkassen eller huske mormors fødselsdag? Opret dine egne opgaver og bliv mindet om alt det du skal huske, når du skal huske det. Lige fra at fodre katten til at nusse med konen.',
    },
    {
      icon: Users,
      color: theme.teal,
      title: 'Fælles opgaver og indkøb',
      text: 'Noget I begge skal huske? Intet problem. Opret fælles opgaver eller tilføj ting til indkøbslisten — så du slipper for at komme hjem fra Netto og mangle de tre liter mælk som din partner mindede dig om i morges.',
    },
  ];

  return (
    <section id="hvordan" style={{
      padding: '60px 20px',
      maxWidth: 1100,
      margin: '0 auto',
    }}>
      <SectionTag theme={theme} text="Sådan virker det" color={theme.coral} />

      <h2 style={sectionHeadingStyle()}>
        Tre trin til en hverdag der ikke kører i ring
      </h2>

      <p style={sectionLeadStyle(theme)}>
        Du behøver ikke krøllede sedler, dårlig samvittighed eller en hukommelse som en elefant.
        I tre trin kører Tag Team af sig selv og sørger for at jeres daglige opgaver bliver løst —
        til tiden, og uden brok.
      </p>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 16,
        marginTop: 40,
      }}>
        {steps.map((s, i) => (
          <div key={i} style={{
            background: theme.bgCard,
            border: `1px solid ${theme.borderSubtle}`,
            borderRadius: 16,
            padding: 24,
            position: 'relative',
          }}>
            <div style={{
              position: 'absolute',
              top: 14, right: 16,
              fontSize: 38,
              fontWeight: 900,
              color: `${s.color}25`,
              lineHeight: 1,
              letterSpacing: -2,
            }}>{i + 1}</div>

            <div style={{
              width: 48, height: 48,
              borderRadius: 12,
              background: `${s.color}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 16,
            }}>
              <s.icon size={22} color={s.color} />
            </div>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{s.title}</h3>
            <p style={{ margin: '8px 0 0', fontSize: 14, color: theme.textMuted, lineHeight: 1.55 }}>{s.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ============================================================
// GAMIFICATION (NEW SECTION ID 103)
// ============================================================

function Gamification({ theme }) {
  const stats = [
    { icon: Star, color: theme.coral, label: 'Giv dine opgaver point' },
    { icon: Heart, color: theme.coral, label: 'Optjen ægte kærlighed' },
    { icon: Flame, color: theme.gold, label: 'Hold streaken gående' },
    { icon: Gift, color: theme.teal, label: 'Sæt præmier på højkant' },
  ];

  return (
    <section id="gamification" style={{
      padding: '60px 20px',
      maxWidth: 1100,
      margin: '0 auto',
    }}>
      <SectionTag theme={theme} text="Gamification" color={theme.coral} />

      <h2 style={sectionHeadingStyle()}>
        En to-do-app der faktisk gør din partner glad
      </h2>

      <p style={sectionLeadStyle(theme)}>
        Vi kombinerer dårlig hukommelse med spil, forøget kærlighed og opgaver der bliver løst.
      </p>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 14,
        marginTop: 40,
      }}>
        {stats.map((s, i) => (
          <div key={i} style={{
            background: `linear-gradient(135deg, ${s.color}10, ${s.color}05)`,
            border: `1px solid ${s.color}30`,
            borderRadius: 16,
            padding: '24px 20px',
            textAlign: 'center',
          }}>
            <div style={{
              width: 56, height: 56,
              borderRadius: 14,
              background: `${s.color}18`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 12px',
            }}>
              <s.icon size={26} color={s.color} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: theme.text, lineHeight: 1.4 }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ============================================================
// FEATURES (UPDATED ID 104)
// ============================================================

function Features({ theme }) {
  const features = [
    { icon: Send, color: theme.coral, title: 'Send opgaver med deadline',
      text: 'Skriv hvad du gerne vil have lavet, vælg hvornår, og send opgaven til din partner. Giv din opgave point som din partner kan optjene ved at løse opgaven.' },
    { icon: Gift, color: theme.coral, title: 'Belønninger — måske som en overraskelse?',
      text: 'Sæt en is, en massage eller en god middag på højkant. Vis præmien med det samme — eller skjul den som overraskelse, der afsløres når opgaven er løst.' },
    { icon: Zap, color: theme.gold, title: 'Point og bonus',
      text: 'Er du hurtigere end deadline, får du bonus-point. Overskrides deadline, halveres dine point. Det skal være sjovt at være hurtig — og fair at være sent på den.' },
    { icon: ShoppingBasket, color: theme.teal, title: 'Fælles indkøbsliste',
      text: 'Begge kan tilføje. Begge kan krydse af. Synkroniseret i realtid. Ikke mere "huskede du mælken?"-sms\'er mens du står i Føtex.' },
    { icon: Calendar, color: theme.teal, title: 'Kalenderoverblik',
      text: 'Se hele måneden visuelt. Farvede prikker viser hvilke opgaver der skal løses hvornår.' },
    { icon: Bell, color: theme.gold, title: 'Smart notifikationer',
      text: 'Få en god start på dagen med det hurtige overblik over dagens opgaver.' },
    { icon: Repeat, color: theme.coral, title: 'Gentagende opgaver',
      text: 'Hver dag, hver uge, hver 14. dag eller en bestemt dato i måneden. Du bestemmer — sæt opgaven op én gang, så kører resten af sig selv.' },
    { icon: UserCircle, color: theme.gold, title: 'For dig selv — og din partner',
      text: 'Få 100% styr på dine daglige rutiner, undgå at glemme en opgave, og gør livet en lille smule lettere.' },
  ];

  return (
    <section id="funktioner" style={{
      padding: '60px 20px',
      maxWidth: 1100,
      margin: '0 auto',
    }}>
      <SectionTag theme={theme} text="Funktioner" color={theme.gold} />

      <h2 style={sectionHeadingStyle()}>
        Alt det I ikke vidste I havde brug for
      </h2>

      <p style={sectionLeadStyle(theme)}>
        Bygget med fokus på at få opgaverne løst og hverdagen gjort nemmere, og konflikterne færre.
      </p>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 14,
        marginTop: 40,
      }}>
        {features.map((f, i) => (
          <div key={i} style={{
            background: theme.bgCard,
            border: `1px solid ${theme.borderSubtle}`,
            borderRadius: 14,
            padding: 20,
          }}>
            <f.icon size={22} color={f.color} style={{ marginBottom: 12 }} />
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{f.title}</h3>
            <p style={{ margin: '6px 0 0', fontSize: 13, color: theme.textMuted, lineHeight: 1.55 }}>{f.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ============================================================
// FAQ (NEW SECTION ID 105)
// ============================================================

function FAQ({ theme }) {
  const items = [
    {
      q: 'Hvornår kan jeg bruge appen?',
      a: 'Appen kan bruges allerede i dag. Den er stadig i BETA version, så hvis du støder på fejl eller har forslag til ændringer, så kontakt os gerne!',
    },
    {
      q: 'Koster det noget?',
      a: 'Nix — alt det du ser er gratis. Tag Team Premium kommer på et tidspunkt med ekstrafunktioner som detaljerede ugerapporter, custom kategorier og familie-tilstand. Beta-testere får rabat når Premium åbner.',
    },
    {
      q: 'Skal vi begge bruge appen?',
      a: 'Nej, faktisk ikke. Tag Team virker fint som personligt todo-program for én bruger — du kan bruge den til at huske dine egne opgaver. Hvis I vil have det fulde par-flow med sendte opgaver, point og fælles indkøbsliste, skal I begge være med. Men det er valgfrit.',
    },
    {
      q: 'Bruger I mine data?',
      a: 'Dine opgaver, beskeder og belønninger er kun synlige for jer to — vi videregiver ikke indhold til tredjepart. På et tidspunkt vil vi, måske, vise reklamer i den gratis version, og det vil indebære en form for tracking (f.eks. anonymiseret information om hvilke kategorier du bruger appen til). Det vil altid være beskrevet i privatlivspolitikken, og du kan fravælge personlige reklamer eller fjerne dem helt med Premium.',
    },
    {
      q: 'Kan børnene være med?',
      a: 'Tag Team v1 er for to voksne. Familie-tilstand med flere brugere (så børn kan have egne opgaver og lommepenge-system) kommer i Premium på et senere tidspunkt.',
    },
    {
      q: 'Hvad sker der hvis jeg ikke løser opgaverne?',
      a: 'Ingenting. Tag Team straffer ikke. Forsinkede opgaver giver halv-point i stedet for fuld-point — det er det. Streaken kan brydes, men kan altid bygges op igen. Det skal være motiverende, ikke stressende.',
    },
  ];

  return (
    <section id="faq" style={{
      padding: '60px 20px',
      maxWidth: 760,
      margin: '0 auto',
    }}>
      <SectionTag theme={theme} text="FAQ" color={theme.teal} />

      <h2 style={sectionHeadingStyle()}>
        Det folk plejer at spørge om
      </h2>

      <div style={{ marginTop: 40, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {items.map((item, i) => (
          <FaqItem key={i} theme={theme} q={item.q} a={item.a} />
        ))}
      </div>
    </section>
  );
}

function FaqItem({ theme, q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{
      background: theme.bgCard,
      border: `1px solid ${theme.borderSubtle}`,
      borderRadius: 12,
      overflow: 'hidden',
    }}>
      <button onClick={() => setOpen(!open)} style={{
        width: '100%',
        padding: '16px 20px',
        background: 'transparent',
        border: 'none',
        color: theme.text,
        textAlign: 'left',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        fontSize: 15,
        fontWeight: 600,
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <HelpCircle size={16} color={theme.coral} style={{ flexShrink: 0 }} />
          {q}
        </span>
        <ChevronDown size={18} color={theme.textMuted} style={{
          flexShrink: 0,
          transform: open ? 'rotate(180deg)' : 'none',
          transition: 'transform 0.2s ease',
        }} />
      </button>
      {open && (
        <div style={{
          padding: '0 20px 18px 46px',
          fontSize: 14,
          color: theme.textMuted,
          lineHeight: 1.6,
        }}>
          {a}
        </div>
      )}
    </div>
  );
}

// ============================================================
// CTA BANDS (used in middle and late sections)
// ============================================================

function CtaBand({ theme, variant }) {
  const headlines = {
    middle: 'Klar til at prøve det?',
    late: 'Stadig her? Så lad os komme i gang',
  };
  const subtexts = {
    middle: 'Det tager 30 sekunder at logge ind, og du er ikke bundet af noget.',
    late: 'Du kan installere appen direkte på din telefon — eller logge ind i browseren.',
  };

  return (
    <section style={{
      padding: '40px 20px',
      maxWidth: 1100,
      margin: '0 auto',
    }}>
      <div style={{
        background: `linear-gradient(135deg, ${theme.coral}12, ${theme.gold}08)`,
        border: `1px solid ${theme.coral}30`,
        borderRadius: 18,
        padding: '32px 24px',
        textAlign: 'center',
      }}>
        <h3 style={{
          margin: 0,
          fontSize: 'clamp(22px, 3.5vw, 28px)',
          fontWeight: 800,
          letterSpacing: -0.5,
        }}>
          {headlines[variant]}
        </h3>
        <p style={{
          margin: '10px auto 22px',
          maxWidth: 480,
          fontSize: 14,
          color: theme.textMuted,
          lineHeight: 1.5,
        }}>
          {subtexts[variant]}
        </p>

        <div style={{
          display: 'flex',
          gap: 10,
          justifyContent: 'center',
          flexWrap: 'wrap',
        }}>
          <a href="/login" style={primaryCtaStyle(theme)}>
            <Sparkles size={16} /> Kom i gang gratis
          </a>
          <InstallAppPrompt theme={theme} variant="hero" />
        </div>
      </div>
    </section>
  );
}

// ============================================================
// SIGNUP CTA (NEWSLETTER)
// ============================================================

function SignupCTA({ theme }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [partnerName, setPartnerName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await submitBetaSignup({ name, email, partnerName, story: null });
      if (result.success) {
        setSubmitted(true);
      } else {
        setError(result.error);
      }
    } catch (e) {
      setError('Noget gik galt — prøv igen om lidt');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section id="kom-i-gang" style={{
      padding: '60px 20px',
      maxWidth: 720,
      margin: '0 auto',
    }}>
      <div style={{
        background: `linear-gradient(135deg, ${theme.coral}15, ${theme.gold}10)`,
        border: `1px solid ${theme.coral}40`,
        borderRadius: 20,
        padding: 32,
      }}>
        {submitted ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
            <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>Tak — du er på listen</h2>
            <p style={{ margin: '12px auto 20px', maxWidth: 420, fontSize: 14, color: theme.textMuted, lineHeight: 1.55 }}>
              Du kan logge ind med Google eller Facebook med det samme og komme i gang.
            </p>
            <a href="/login" style={{
              display: 'inline-block',
              padding: '12px 24px',
              background: theme.coral,
              color: 'white',
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 700,
              textDecoration: 'none',
            }}>
              Log ind og kom i gang →
            </a>
          </div>
        ) : (
          <>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <h2 style={{ margin: 0, fontSize: 'clamp(22px, 4vw, 30px)', fontWeight: 800, letterSpacing: -0.5 }}>
                Hold dig opdateret
              </h2>
              <p style={{ margin: '10px auto 0', maxWidth: 460, fontSize: 14, color: theme.textMuted, lineHeight: 1.5 }}>
                Skriv dig op til vores nyhedsbrev, så holder vi dig opdateret med tips og nye funktioner.
                Ellers kan du <a href="/login" style={{ color: theme.coral, textDecoration: 'underline' }}>logge ind</a> med det samme og prøve appen.
              </p>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <FormField label="Dit navn *">
                <input type="text" required value={name} onChange={e => setName(e.target.value)}
                  placeholder="Anders" style={inputStyle(theme)} />
              </FormField>

              <FormField label="Din email *">
                <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="anders@example.com" style={inputStyle(theme)} />
              </FormField>

              <FormField label="Partners navn (valgfri)">
                <input type="text" value={partnerName} onChange={e => setPartnerName(e.target.value)}
                  placeholder="Sandie" style={inputStyle(theme)} />
              </FormField>

              {error && (
                <div style={{
                  padding: '10px 12px',
                  background: 'rgba(239, 68, 68, 0.15)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: '#F87171',
                  fontSize: 12,
                  borderRadius: 8,
                }}>{error}</div>
              )}

              <button type="submit" disabled={submitting} style={{
                marginTop: 6,
                padding: '14px 16px',
                background: theme.coral,
                color: 'white',
                border: 'none',
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 700,
                cursor: submitting ? 'wait' : 'pointer',
                opacity: submitting ? 0.7 : 1,
              }}>
                {submitting ? 'Tilmelder...' : 'Skriv mig op'}
              </button>

              <p style={{ margin: '6px 0 0', fontSize: 11, color: theme.textDim, textAlign: 'center', lineHeight: 1.5 }}>
                Ved at tilmelde dig accepterer du vores <a href="/privatlivspolitik" style={{ color: theme.textMuted }}>privatlivspolitik</a>.
                Vi sender kun beskeder om Tag Team.
              </p>
            </form>
          </>
        )}
      </div>
    </section>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <label style={{
        display: 'block',
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        color: '#A19B8F',
        marginBottom: 6,
      }}>{label}</label>
      {children}
    </div>
  );
}

function inputStyle(theme) {
  return {
    width: '100%',
    padding: '11px 14px',
    background: theme.bg,
    border: `1px solid ${theme.border}`,
    borderRadius: 10,
    color: theme.text,
    fontSize: 14,
    outline: 'none',
    fontFamily: 'inherit',
  };
}

// ============================================================
// FOOTER
// ============================================================

function Footer({ theme }) {
  return (
    <footer style={{
      padding: '40px 20px',
      borderTop: `1px solid ${theme.border}`,
      marginTop: 40,
    }}>
      <div style={{
        maxWidth: 1100,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
      }}>
        <Logo size={28} theme={theme} />

        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', justifyContent: 'center' }}>
          <a href="/privatlivspolitik" style={footerLinkStyle(theme)}>Privatlivspolitik</a>
          <a href="/handelsbetingelser" style={footerLinkStyle(theme)}>Handelsbetingelser</a>
          <a href="/data-deletion" style={footerLinkStyle(theme)}>Sletning af data</a>
          <a href="mailto:hej@gettagteam.com" style={footerLinkStyle(theme)}>Kontakt</a>
        </div>

        <div style={{ fontSize: 11, color: theme.textDim, textAlign: 'center', lineHeight: 1.6 }}>
          © {new Date().getFullYear()} Anka Media · Tag Team<br />
          Get sh*it done · Mindre stress · mere kærlighed
        </div>
      </div>
    </footer>
  );
}

function footerLinkStyle(theme) {
  return {
    color: theme.textMuted,
    textDecoration: 'none',
    fontSize: 13,
    fontWeight: 500,
  };
}

// ============================================================
// SHARED HELPERS
// ============================================================

function SectionTag({ theme, text, color }) {
  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '5px 12px',
      background: `${color}15`,
      border: `1px solid ${color}30`,
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 1,
      textTransform: 'uppercase',
      color,
      marginBottom: 14,
    }}>
      {text}
    </div>
  );
}

function sectionHeadingStyle() {
  return {
    margin: 0,
    fontSize: 'clamp(26px, 4vw, 38px)',
    fontWeight: 800,
    letterSpacing: -0.8,
    lineHeight: 1.15,
    maxWidth: 740,
  };
}

function sectionLeadStyle(theme) {
  return {
    margin: '14px 0 0',
    maxWidth: 600,
    fontSize: 15,
    color: theme.textMuted,
    lineHeight: 1.6,
  };
}

function primaryCtaStyle(theme) {
  return {
    padding: '14px 24px',
    background: theme.coral,
    color: 'white',
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 700,
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    border: 'none',
    cursor: 'pointer',
  };
}

function secondaryCtaStyle(theme) {
  return {
    padding: '14px 24px',
    background: 'transparent',
    color: theme.text,
    border: `1px solid ${theme.border}`,
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 600,
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
  };
}

// ============================================================
// LOGO
// ============================================================

function Logo({ size = 32, theme }) {
  return (
    <svg width={size} height={size} viewBox="0 0 120 120">
      <g transform="translate(60,60)">
        <rect x="-30" y="-20" width="20" height="40" rx="3" fill={theme.coral}/>
        <rect x="-36" y="-20" width="32" height="9" rx="3" fill={theme.coral}/>
        <rect x="6" y="-22" width="20" height="40" rx="3" fill={theme.gold}/>
        <rect x="0" y="-22" width="32" height="9" rx="3" fill={theme.gold}/>
      </g>
    </svg>
  );
}
